/**
 * ClubMembers.js — Onda 110
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Os associados do clube, vistos de dentro da plataforma: quem está no
 * cadastro, com quais veículos, de qual carga veio — e a edição à mão quando
 * for preciso.
 *
 * Até a Onda 110, o Clube Social não tinha nenhuma tela de cadastro: os
 * associados entram pela API do sistema de gestão, e conferir o que havia
 * dentro exigia abrir o banco. Foi o que a auditoria de 22/09/2026 apontou.
 *
 * Sobre editar: a carga seguinte sobrescreve nome e situação do associado, e
 * placa, marca, modelo e situação do veículo. A tela não esconde isso — avisa
 * antes de salvar e marca o registro como editado aqui, com quem e quando.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Pagination from '../components/Pagination';
import PlacaVeicular from '../components/PlacaVeicular';   // Onda 190
import EvidenciaDoAcesso from '../components/EvidenciaDoAcesso';   // Onda 191
import '../styles/BuscaPorPlaca.css';                      // Onda 190
import { toast } from 'react-hot-toast';
import {
  FiUsers, FiSearch, FiX, FiRefreshCw, FiEdit2, FiSave, FiAlertTriangle,
  FiTruck, FiClock, FiUploadCloud,
} from 'react-icons/fi';
import './ClubMembers.css';

const dataHora = (v) => (v ? new Date(v).toLocaleString('pt-BR') : '—');
const numero = (n) => (Number(n) || 0).toLocaleString('pt-BR');
const situacao = (s) => (s === 'active' ? 'Ativo' : 'Inativo');

export default function ClubMembers() {
  const { user } = useAuth();
  const podeEditar = user?.role === 'admin' || user?.role === 'superadmin';

  const [lista, setLista] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(50);
  const [status, setStatus] = useState('');
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [edicao, setEdicao] = useState(null);
  const abortRef = useRef(null);

  // A busca digitada não pode disparar uma requisição por tecla.
  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaDigitada), 450);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  const buscarLista = useCallback(async (paginaAlvo = 1, tamanhoAlvo = tamanho) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCarregando(true);
    try {
      const p = new URLSearchParams({
        limit: String(tamanhoAlvo), offset: String((paginaAlvo - 1) * tamanhoAlvo),
      });
      if (busca) p.append('search', busca);
      if (status) p.append('status', status);
      const r = await api.get(`/api/club-members?${p.toString()}`, { signal: controller.signal });
      setLista(r.data.items || []);
      setResumo(r.data.summary || null);
      setTotal(r.data.total || 0);
      setPagina(paginaAlvo);
    } catch (e) {
      if (e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
      toast.error(e.response?.data?.error || 'Não foi possível carregar os associados');
    } finally {
      if (abortRef.current === controller) setCarregando(false);
    }
  }, [busca, status, tamanho]);

  useEffect(() => { buscarLista(1); }, [buscarLista]);

  const abrirDetalhe = async (id) => {
    try {
      const r = await api.get(`/api/club-members/${id}`);
      setDetalhe(r.data);
      setEdicao(null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível abrir o associado');
    }
  };

  const salvarAssociado = async () => {
    try {
      await api.put(`/api/club-members/${detalhe.id}`, { name: edicao.name, status: edicao.status });
      toast.success('Associado atualizado. A próxima carga do sistema de gestão pode substituir.');
      await abrirDetalhe(detalhe.id);
      buscarLista(pagina);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível salvar');
    }
  };

  const salvarVeiculo = async (veiculo, mudancas) => {
    try {
      await api.put(`/api/club-members/vehicles/${veiculo.id}`, mudancas);
      toast.success('Veículo atualizado. A próxima carga pode substituir.');
      await abrirDetalhe(detalhe.id);
      buscarLista(pagina);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível salvar o veículo');
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(total / tamanho));

  return (
    <div className="cm-page">
      <div className="page-header">
        <div>
          <h1><FiUsers /> Associados</h1>
          <p>O cadastro que o sistema de gestão do clube envia pela API de Integração</p>
        </div>
        <button className="btn btn-secondary" onClick={() => buscarLista(pagina)} disabled={carregando}>
          <FiRefreshCw className={carregando ? 'spin' : ''} /> Atualizar
        </button>
      </div>

      {resumo && (
        <div className="cm-resumo">
          <div><span className="n">{numero(resumo.associados)}</span><span className="r">associados</span></div>
          <div><span className="n">{numero(resumo.ativos)}</span><span className="r">ativos</span></div>
          <div><span className="n">{numero(resumo.veiculos)}</span><span className="r">veículos</span></div>
          <div><span className="n">{numero(resumo.veiculos_ativos)}</span><span className="r">veículos ativos</span></div>
          <div className={resumo.editados + resumo.veiculos_editados > 0 ? 'marcado' : ''}>
            <span className="n">{numero(resumo.editados + resumo.veiculos_editados)}</span>
            <span className="r">editados aqui</span>
          </div>
        </div>
      )}

      <div className="cm-filtros">
        <div className="cm-busca">
          <FiSearch />
          <input placeholder="Nome, código do associado ou placa"
            value={buscaDigitada} onChange={e => setBuscaDigitada(e.target.value)} />
          {buscaDigitada && <button onClick={() => setBuscaDigitada('')} aria-label="Limpar busca"><FiX /></button>}
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Situação">
          <option value="">Todos</option>
          <option value="active">Ativos</option>
          <option value="inactive">Inativos</option>
        </select>
      </div>

      {!carregando && lista.length === 0 && (
        <div className="cm-vazio">
          <FiUploadCloud size={44} />
          <h3>Nenhum associado no cadastro</h3>
          <p>
            Os associados chegam pela API de Integração. O resultado de cada envio fica em
            Integração → Cargas da Integração, com o que entrou e o que foi recusado.
          </p>
        </div>
      )}

      {lista.length > 0 && (
        <>
          <div className="cm-tabela-rolo">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Código</th><th>Associado</th><th>Situação</th>
                  <th>Veículos</th><th>Origem</th><th>Atualizado</th><th></th>
                </tr>
              </thead>
              <tbody>
                {lista.map(a => (
                  <tr key={a.id} className={a.status === 'inactive' ? 'cm-inativo' : ''}>
                    <td className="cm-mono">{a.member_code}</td>
                    <td>
                      {a.name}
                      {a.edited_at && (
                        <span className="cm-etiqueta editado" title={`Editado aqui em ${dataHora(a.edited_at)}${a.edited_by_name ? ` por ${a.edited_by_name}` : ''}`}>
                          editado aqui
                        </span>
                      )}
                      {/* Onda 190: os veículos cuja placa bateu com a busca, logo abaixo do nome. */}
                      {a.matched_vehicles && a.matched_vehicles.length > 0 && (
                        <ul className="busca-placas">
                          {a.matched_vehicles.map(v => (
                            <li key={v.id} onClick={() => abrirDetalhe(a.id)} title="Abrir o cadastro do associado">
                              <PlacaVeicular placa={v.plate} tamanho="p" />
                              <span className="busca-placas-modelo">{[v.brand, v.model].filter(Boolean).join(' ') || 'marca e modelo não informados'}</span>
                              <span className={`busca-placas-situacao ${v.status === 'active' ? 'ativo' : 'inativo'}`}>{v.status === 'active' ? 'veículo ativo' : 'veículo inativo'}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>
                      <span className={`cm-situacao ${a.status}`}>{situacao(a.status)}</span>
                    </td>
                    <td className="cm-centro">
                      {a.vehicles_active} de {a.vehicles}
                    </td>
                    <td className="cm-origem">
                      {a.source_api_key_name
                        ? <span title={a.last_seen_sync_id ? `Última carga declarada: ${a.last_seen_sync_id}` : 'Carga sem execução declarada'}>API · {a.source_api_key_name}</span>
                        : <span className="cm-manual">cadastro local</span>}
                    </td>
                    <td>{dataHora(a.updated_at)}</td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={() => abrirDetalhe(a.id)}>Abrir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={pagina}
            totalPages={totalPaginas}
            pageSize={tamanho}
            totalRecords={total}
            onPageChange={(p) => buscarLista(p)}
            onPageSizeChange={(t) => { setTamanho(t); buscarLista(1, t); }}
            itemLabel="associado"
          />
        </>
      )}

      {detalhe && (
        <div className="cm-modal-fundo" onClick={() => setDetalhe(null)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <div className="cm-modal-cabeca">
              <div>
                <h2>{detalhe.name}</h2>
                <span className="cm-mono">{detalhe.member_code}</span>
              </div>
              <button className="cm-fechar" onClick={() => setDetalhe(null)} aria-label="Fechar"><FiX /></button>
            </div>

            <div className="cm-modal-corpo">
              <div className="cm-origem-bloco">
                <p>
                  <strong>Origem:</strong>{' '}
                  {detalhe.source_api_key_name
                    ? `carregado pela API com a chave "${detalhe.source_api_key_name}"`
                    : 'cadastrado dentro da plataforma'}
                  {detalhe.last_seen_sync_id
                    ? ` · última execução declarada: ${detalhe.last_seen_sync_id}`
                    : detalhe.source_api_key_name ? ' · sem execução declarada' : ''}
                </p>
                <p>
                  <strong>Criado em</strong> {dataHora(detalhe.created_at)} ·{' '}
                  <strong>atualizado em</strong> {dataHora(detalhe.updated_at)}
                  {detalhe.external_updated_at && <> · <strong>no sistema de gestão:</strong> {dataHora(detalhe.external_updated_at)}</>}
                </p>
                {detalhe.edited_at && (
                  <p className="cm-aviso-editado">
                    <FiEdit2 /> Editado aqui em {dataHora(detalhe.edited_at)}
                    {detalhe.edited_by_name ? ` por ${detalhe.edited_by_name}` : ''}
                    {detalhe.edited_fields ? ` (${detalhe.edited_fields})` : ''}.
                  </p>
                )}
              </div>

              {podeEditar && (
                <section className="cm-secao">
                  <h3>Editar associado</h3>
                  <p className="cm-aviso">
                    <FiAlertTriangle /> O sistema de gestão do clube é o dono deste cadastro:
                    a próxima carga substitui o nome e a situação que você alterar aqui.
                    A edição fica registrada com o seu usuário.
                  </p>
                  <div className="cm-form">
                    <label>
                      Nome
                      <input value={edicao?.name ?? detalhe.name}
                        onChange={e => setEdicao({ ...(edicao || { status: detalhe.status }), name: e.target.value })} />
                    </label>
                    <label>
                      Situação
                      <select value={edicao?.status ?? detalhe.status}
                        onChange={e => setEdicao({ ...(edicao || { name: detalhe.name }), status: e.target.value })}>
                        <option value="active">Ativo</option>
                        <option value="inactive">Inativo</option>
                      </select>
                    </label>
                    <button className="btn btn-primary" disabled={!edicao} onClick={salvarAssociado}>
                      <FiSave /> Salvar
                    </button>
                  </div>
                </section>
              )}

              <section className="cm-secao">
                <h3><FiTruck /> Veículos ({detalhe.vehicles.length})</h3>
                <div className="cm-tabela-rolo">
                  <table className="report-table">
                    <thead>
                      <tr><th>Placa</th><th>Marca / Modelo</th><th>Situação</th><th>Último acesso</th><th>Atualizado</th>{podeEditar && <th></th>}</tr>
                    </thead>
                    <tbody>
                      {detalhe.vehicles.map(v => (
                        <VeiculoLinha key={v.id} veiculo={v} podeEditar={podeEditar} onSalvar={salvarVeiculo} />
                      ))}
                      {detalhe.vehicles.length === 0 && (
                        <tr><td colSpan={podeEditar ? 6 : 5} className="cm-centro">Sem veículos no cadastro.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {detalhe.ultimas_leituras.length > 0 && (
                <section className="cm-secao">
                  <h3><FiClock /> Últimas passagens</h3>
                  <ul className="cm-leituras">
                    {detalhe.ultimas_leituras.map((l, i) => (
                      <li key={i}>
                        <span className="plate-mono">{l.plate}</span>
                        {l.log_type === 'entrada' ? ' entrou ' : ' saiu '}
                        em {dataHora(l.detected_at)}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Uma linha de veículo, que vira formulário quando o gestor clica em editar. */
function VeiculoLinha({ veiculo, podeEditar, onSalvar }) {
  const [editando, setEditando] = useState(false);
  const [campos, setCampos] = useState({
    plate: veiculo.plate, brand: veiculo.brand || '', model: veiculo.model || '', status: veiculo.status,
  });

  if (!editando) {
    return (
      <tr className={veiculo.status === 'inactive' ? 'cm-inativo' : ''}>
        <td>
          <span className="plate-mono">{veiculo.plate}</span>
          {veiculo.edited_at && <span className="cm-etiqueta editado" title={`Editado aqui em ${dataHora(veiculo.edited_at)}`}>editado aqui</span>}
        </td>
        <td>{[veiculo.brand, veiculo.model].filter(Boolean).join(' ') || '—'}</td>
        <td><span className={`cm-situacao ${veiculo.status}`}>{situacao(veiculo.status)}</span></td>
        <td><EvidenciaDoAcesso acesso={veiculo.last_access} placa={veiculo.plate} /></td>
        <td>{dataHora(veiculo.updated_at)}</td>
        {podeEditar && (
          <td><button className="btn btn-sm btn-secondary" onClick={() => setEditando(true)}><FiEdit2 /> Editar</button></td>
        )}
      </tr>
    );
  }

  return (
    <tr>
      <td><input className="cm-campo-curto" value={campos.plate}
        onChange={e => setCampos({ ...campos, plate: e.target.value.toUpperCase() })} /></td>
      <td>
        <input className="cm-campo-curto" placeholder="Marca" value={campos.brand}
          onChange={e => setCampos({ ...campos, brand: e.target.value })} />
        <input className="cm-campo-curto" placeholder="Modelo" value={campos.model}
          onChange={e => setCampos({ ...campos, model: e.target.value })} />
      </td>
      <td>
        <select value={campos.status} onChange={e => setCampos({ ...campos, status: e.target.value })}>
          <option value="active">Ativo</option>
          <option value="inactive">Inativo</option>
        </select>
      </td>
      <td><EvidenciaDoAcesso acesso={veiculo.last_access} placa={veiculo.plate} /></td>
      <td colSpan={2}>
        <button className="btn btn-sm btn-primary" onClick={() => { onSalvar(veiculo, campos); setEditando(false); }}>
          <FiSave /> Salvar
        </button>
        <button className="btn btn-sm btn-secondary" onClick={() => setEditando(false)}>Cancelar</button>
      </td>
    </tr>
  );
}
